import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pin, Plus, Search, Archive, Eye, EyeOff, LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import { api, toastError } from "../../lib/api";
import { useEffectiveRole, canAtLeast } from "../../lib/roles";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";

type Note = {
  id: string; content: string | null; category: string; pinned: boolean;
  managers_only: boolean; is_private?: boolean; can_reveal?: boolean; can_edit?: boolean;
  author_name: string; created_at: string; updated_at?: string | null;
};

const CATEGORIES = [
  "personal", "family", "interests", "preferences", "hospitality", "conversation_follow_up",
];

export default function ProfilingNotesCard({ contactId }: { contactId: string }) {
  const role = useEffectiveRole();
  const canEdit = canAtLeast(role, "representative");
  const canRestrict = canAtLeast(role, "manager_trainer");
  const [notes, setNotes] = useState<Note[]>([]);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [revealing, setRevealing] = useState<string | null>(null);
  const [form, setForm] = useState({
    content: "", category: "personal", pinned: false, managers_only: false, is_private: false,
  });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/crm/contacts/${contactId}/360`);
      setNotes(Array.isArray(data?.profiling_notes) ? data.profiling_notes : []);
    } catch (e) { toastError(e); }
  }, [contactId]);
  useEffect(() => { setRevealed({}); load(); }, [load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = q ? notes.filter((n) =>
      `${n.is_private ? "" : (n.content || "")} ${n.category} ${n.author_name}`.toLowerCase().includes(q)) : notes;
    return showAll || q ? rows : rows.slice(0, 3);
  }, [notes, query, showAll]);

  async function addNote() {
    if (!form.content.trim()) return;
    setSaving(true);
    try {
      await api.post(`/crm/contacts/${contactId}/profiling-notes`, {
        ...form, content: form.content.trim(),
      });
      toast.success("Profiling note added");
      setForm({ content: "", category: "personal", pinned: false, managers_only: false, is_private: false });
      setOpen(false);
      await load();
    } catch (e) { toastError(e); } finally { setSaving(false); }
  }

  async function toggleReveal(note: Note) {
    if (revealed[note.id] !== undefined) {
      setRevealed((current) => {
        const next = { ...current };
        delete next[note.id];
        return next;
      });
      return;
    }
    setRevealing(note.id);
    try {
      const { data } = await api.get(`/crm/contacts/${contactId}/profiling-notes/${note.id}/reveal`);
      setRevealed((current) => ({ ...current, [note.id]: data.content || "" }));
    } catch (e) { toastError(e); } finally { setRevealing(null); }
  }

  async function archiveNote(id: string) {
    try {
      await api.post(`/crm/contacts/${contactId}/profiling-notes/${id}/archive`);
      toast.success("Profiling note archived");
      await load();
    } catch (e) { toastError(e); }
  }

  return (
    <section className="border border-slate-200 rounded-lg bg-white p-4" data-testid="profiling-notes-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div>
          <h2 className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Profiling notes</h2>
          <p className="text-xs text-slate-500 mt-1">Relationship cues for thoughtful conversations.</p>
        </div>
        {canEdit && <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="add-profiling-note">
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>}
      </div>
      {(showAll || notes.length > 3) && (
        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input className="pl-8" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search notes" aria-label="Search profiling notes" />
        </div>
      )}
      {visible.length === 0 ? <p className="text-sm text-slate-500">No profiling notes yet.</p> :
        <div className="space-y-3">
          {visible.map((note) => (
            <article key={note.id} className="rounded-md border border-slate-100 bg-slate-50/60 p-3" data-testid={`profiling-note-${note.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  {note.is_private ? (
                    <div>
                      <div className="flex items-center gap-1.5 text-xs font-medium text-violet-700 mb-1">
                        <LockKeyhole className="w-3.5 h-3.5" /> Private
                      </div>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid={`private-note-body-${note.id}`}>
                        {revealed[note.id] !== undefined ? revealed[note.id] : "••••••••••••••••"}
                      </p>
                    </div>
                  ) : <p className="text-sm text-slate-800 whitespace-pre-wrap">{note.content}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {note.is_private && note.can_reveal && (
                    <button type="button" className="rounded p-1 text-slate-500 hover:text-slate-900"
                      onClick={() => toggleReveal(note)} disabled={revealing === note.id}
                      aria-label={revealed[note.id] !== undefined ? "Hide private profiling note" : "View private profiling note"}
                      data-testid={`toggle-private-note-${note.id}`}>
                      {revealed[note.id] !== undefined ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                  {note.pinned && <Pin className="w-4 h-4 text-amber-600" aria-label="Pinned" />}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                <span className="capitalize">{note.category.replace(/_/g, " ")}</span>
                <span>• {note.author_name}</span>
                <span>• {new Date(note.created_at).toLocaleString()}</span>
                {note.updated_at && note.updated_at !== note.created_at && <span>• edited</span>}
                {note.managers_only && <span>• managers only</span>}
                {canEdit && (note.can_edit ?? true) && <button className="ml-auto hover:text-slate-900" onClick={() => archiveNote(note.id)}
                  aria-label="Archive profiling note"><Archive className="w-3.5 h-3.5" /></button>}
              </div>
            </article>
          ))}
        </div>}
      {notes.length > 3 && <Button className="mt-3 px-0" variant="link" onClick={() => setShowAll((v) => !v)}>
        {showAll ? "Show recent notes" : `View all profiling notes (${notes.length})`}
      </Button>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="profiling-note-dialog">
          <DialogHeader><DialogTitle>Add profiling note</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Relationship detail</Label><Textarea value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Example: Enjoys hockey and supports the Edmonton Oilers." maxLength={2000} rows={4} /></div>
            <div><Label>Category</Label><Select value={form.category}
              onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pinned}
              onChange={(e) => setForm({ ...form, pinned: e.target.checked })} /> Pin as important to remember</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_private}
              onChange={(e) => setForm({ ...form, is_private: e.target.checked, managers_only: e.target.checked ? false : form.managers_only })} />
              Private — only me and Tenant Owners can reveal</label>
            {canRestrict && !form.is_private && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.managers_only}
              onChange={(e) => setForm({ ...form, managers_only: e.target.checked })} /> Managers only</label>}
            <p className="text-xs text-slate-500">Record useful, respectful relationship context—not gossip or unnecessary sensitive details.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={addNote} disabled={saving || !form.content.trim()}>{saving ? "Saving…" : "Add note"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
